/**
 * AddOnsTab.js
 * System Admin tab: complimentary billable add-ons + a la carte module access.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Icons } from '../common/Icons';
import { tenantsService } from '../../services/api';

function fmtCents(cents) {
  if (cents == null) return '—';
  return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function AddonStatusBadge({ addon }) {
  if (!addon?.active) {
    return <span className="badge badge-neutral">Inactive</span>;
  }
  if (addon.source === 'stripe') {
    return <span className="badge badge-success">Active (Stripe)</span>;
  }
  return <span className="badge badge-success">Active (complimentary)</span>;
}

function Toggle({ checked, disabled, onChange, label }) {
  return (
    <label
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        fontSize: 13,
        userSelect: 'none',
      }}
    >
      <input
        type="checkbox"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: 16, height: 16 }}
      />
      {label}
    </label>
  );
}

export default function AddOnsTab({ tenant }) {
  const [addons, setAddons] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busySlug, setBusySlug] = useState(null);
  const [moduleBusy, setModuleBusy] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    if (!tenant?.id) return;
    setLoading(true);
    setError(null);
    try {
      const [addonsRes, modulesRes] = await Promise.all([
        tenantsService.getAddons(tenant.id),
        tenantsService.getModules(tenant.id),
      ]);
      setAddons(addonsRes.data?.addons || []);
      setModules(modulesRes.data?.modules || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load add-ons');
    } finally {
      setLoading(false);
    }
  }, [tenant?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const grant = async (slug) => {
    setBusySlug(slug);
    setNotice(null);
    setError(null);
    try {
      const res = await tenantsService.grantAddon(tenant.id, slug);
      setNotice(res.message || 'Complimentary add-on granted');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Grant failed');
    } finally {
      setBusySlug(null);
    }
  };

  const revoke = async (slug) => {
    if (!window.confirm('Revoke complimentary access for this add-on?')) return;
    setBusySlug(slug);
    setNotice(null);
    setError(null);
    try {
      const res = await tenantsService.revokeAddon(tenant.id, slug);
      setNotice(res.message || 'Complimentary add-on revoked');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Revoke failed');
    } finally {
      setBusySlug(null);
    }
  };

  const updateModule = async (slug, patch) => {
    setModuleBusy(slug + ':' + Object.keys(patch).join(','));
    setError(null);
    try {
      const res = await tenantsService.updateModule(tenant.id, slug, patch);
      const updated = res.data?.module;
      if (updated) {
        setModules((prev) => prev.map((m) => (m.slug === slug ? { ...m, ...updated } : m)));
      } else {
        await load();
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Module update failed');
      await load();
    } finally {
      setModuleBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="empty-state">
        <Icons.Loader size={40} className="spin" />
        <p>Loading add-ons…</p>
        <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {error && (
        <div
          className="card"
          style={{
            borderColor: 'var(--danger, #ef4444)',
            color: 'var(--danger, #ef4444)',
            padding: '12px 16px',
          }}
        >
          {error}
        </div>
      )}
      {notice && (
        <div
          className="card"
          style={{
            borderColor: 'var(--success, #22c55e)',
            color: 'var(--success, #22c55e)',
            padding: '12px 16px',
          }}
        >
          {notice}
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
          <h3 className="card-title" style={{ margin: 0 }}>Billable add-ons</h3>
          <button className="btn btn-secondary btn-sm" onClick={load} type="button">
            <Icons.RefreshCw size={14} />
            Refresh
          </button>
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 0, marginBottom: 20 }}>
          Grant complimentary access for demos and special cases. Paid self-serve checkout stays in the Tenant Portal.
          The Subscription tab remains base plan only. Change the platform list price under{' '}
          <strong>Settings → Billable Add-ons</strong>.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {addons.map((addon) => {
            const busy = busySlug === addon.slug;
            return (
              <div
                key={addon.slug}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 16,
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px 0',
                  borderTop: '1px solid var(--border-color)',
                }}
              >
                <div style={{ flex: '1 1 280px', minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <strong style={{ fontSize: 16 }}>{addon.name}</strong>
                    <AddonStatusBadge addon={addon} />
                  </div>
                  <p style={{ margin: '0 0 8px', color: 'var(--text-muted)', fontSize: 13 }}>
                    {addon.description}
                  </p>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                    {fmtCents(addon.price_cents)}/mo
                    {' · '}
                    Provisioned {fmtDate(addon.provisioned_at)}
                    {addon.stripe_subscription_id ? ` · Stripe ${addon.stripe_subscription_id}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {addon.can_grant_complimentary && (
                    <button
                      className="btn btn-primary"
                      type="button"
                      disabled={busy}
                      onClick={() => grant(addon.slug)}
                    >
                      {busy ? <Icons.Loader size={16} className="spin" /> : <Icons.Plus size={16} />}
                      Grant complimentary
                    </button>
                  )}
                  {addon.can_revoke_complimentary && (
                    <button
                      className="btn btn-secondary"
                      type="button"
                      disabled={busy}
                      onClick={() => revoke(addon.slug)}
                    >
                      {busy ? <Icons.Loader size={16} className="spin" /> : <Icons.X size={16} />}
                      Revoke complimentary
                    </button>
                  )}
                  {addon.active && addon.source === 'stripe' && (
                    <span style={{ fontSize: 13, color: 'var(--text-muted)', alignSelf: 'center' }}>
                      Managed via Stripe / Portal
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          {addons.length === 0 && (
            <p style={{ color: 'var(--text-muted)' }}>No billable add-ons configured.</p>
          )}
        </div>
      </div>

      <div className="card">
        <h3 className="card-title" style={{ marginTop: 0 }}>Module access</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 0, marginBottom: 16 }}>
          Override tier access (`granted_override`) or toggle whether the module is enabled for this tenant.
          Back Office is always included and cannot be turned off.
        </p>

        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border-color)' }}>
                <th style={{ padding: '10px 8px', fontSize: 13 }}>Module</th>
                <th style={{ padding: '10px 8px', fontSize: 13 }}>Category</th>
                <th style={{ padding: '10px 8px', fontSize: 13 }}>Min tier</th>
                <th style={{ padding: '10px 8px', fontSize: 13 }}>Override</th>
                <th style={{ padding: '10px 8px', fontSize: 13 }}>Enabled</th>
              </tr>
            </thead>
            <tbody>
              {modules.map((mod) => {
                const overrideBusy = moduleBusy === `${mod.slug}:granted_override`;
                const enabledBusy = moduleBusy === `${mod.slug}:is_enabled`;
                const lockedOn = !!mod.always_on;
                return (
                  <tr key={mod.slug} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '12px 8px' }}>
                      <div style={{ fontWeight: 600 }}>{mod.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {mod.slug}{lockedOn ? ' · always included' : ''}
                      </div>
                    </td>
                    <td style={{ padding: '12px 8px', fontSize: 13, color: 'var(--text-muted)' }}>
                      {mod.category || '—'}
                    </td>
                    <td style={{ padding: '12px 8px', fontSize: 13 }}>{mod.min_plan_tier ?? '—'}</td>
                    <td style={{ padding: '12px 8px' }}>
                      <Toggle
                        label={overrideBusy ? 'Saving…' : lockedOn ? 'Always' : 'Granted'}
                        checked={!!mod.granted_override || lockedOn}
                        disabled={!!moduleBusy || lockedOn}
                        onChange={(val) => updateModule(mod.slug, { granted_override: val })}
                      />
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      <Toggle
                        label={enabledBusy ? 'Saving…' : lockedOn ? 'Always' : 'Enabled'}
                        checked={!!mod.is_enabled || lockedOn}
                        disabled={!!moduleBusy || lockedOn}
                        onChange={(val) => updateModule(mod.slug, { is_enabled: val })}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {modules.length === 0 && (
            <p style={{ color: 'var(--text-muted)' }}>No modules found in the app registry.</p>
          )}
        </div>
      </div>

      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
